import { P } from "./permissions";

/**
 * Frontend-only starting points for the permission drawer. Applying a preset replaces the
 * drawer's toggles; nothing reaches the server until the drawer is saved, so these presets
 * never change what a role can do on their own.
 *
 * Two deliberate shapes run through the lists below:
 *
 * - Case modules (clients, admissions, visa, insurance, RPL clients, skill assessment) get
 *   read, create and update but not delete. Deleting a case record clears dependent rows
 *   (terms, payments, verifications), which the permission descriptions treat as supervisor
 *   work rather than day-to-day work.
 * - Catalogues an admin is expected to maintain (colleges, courses, price list, check list)
 *   get delete as well, because maintaining a catalogue means removing stale entries.
 *
 * ACCOUNT is ADMIN plus the money-side keys. SUPER_ADMIN is every organisation permission the
 * drawer offers. Every other role starts empty and is granted key by key.
 */

const ADMIN_KEYS = [
  P.DASHBOARD.READ,

  // Clients and their documents, which admins manage end to end
  P.CLIENTS.READ,
  P.CLIENTS.CREATE,
  P.CLIENTS.UPDATE,
  P.CLIENTS.ASSIGN_OFFICER,
  P.CLIENT_DOCUMENT.READ,
  P.CLIENT_DOCUMENT.CREATE,
  P.CLIENT_DOCUMENT.UPDATE,
  P.CLIENT_DOCUMENT.DELETE,
  P.CLIENT_DOCUMENT_REQUEST.CREATE,
  P.CLIENT_DOCUMENT_REQUEST.REVOKE,

  // Case modules, opened progressed and closed by the admin who owns the case
  P.ADMISSIONS.READ,
  P.ADMISSIONS.CREATE,
  P.ADMISSIONS.UPDATE,
  P.ADMISSIONS.DELETE,
  P.VISA.READ,
  P.VISA.CREATE,
  P.VISA.UPDATE,
  P.VISA.DELETE,
  P.VISA_DOCUMENT.READ,
  P.INSURANCE.READ,
  P.INSURANCE.CREATE,
  P.INSURANCE.UPDATE,
  P.INSURANCE.DELETE,
  P.RPL_CLIENT.READ,
  P.RPL_CLIENT.CREATE,
  P.RPL_CLIENT.UPDATE,
  P.RPL_CLIENT.DELETE,
  P.SKILL.READ,
  P.SKILL.CREATE,
  P.SKILL.UPDATE,
  P.SKILL.DELETE,

  // Money they raise but do not approve: record a payment, refund one, watch its verification
  // status and chase it. Approving is accounts:payment:verify, which stays with Account.
  // Provider read is view-only (who the providers are and what is owed); creating, updating or
  // deleting providers stays with Account.
  P.ACCOUNTS.CLIENT_MANAGE,
  P.ACCOUNTS.PAYMENT_CREATE,
  P.ACCOUNTS.PAYMENT_READ,
  P.ACCOUNTS.PAYMENT_REFUND,
  P.ACCOUNTS.PROVIDER_READ,
  P.ACCOUNTS.VERIFICATION_READ,
  P.ACCOUNTS.VERIFICATION_SEND,
  P.ACCOUNTS.NOTE_READ,
  P.ACCOUNTS.NOTE_CREATE,
  P.ACCOUNTS.NOTE_UPDATE,
  P.ACCOUNTS.NOTE_DELETE,
  P.INVOICE.CLIENT_READ,
  P.INVOICE.CLIENT_CREATE,
  // Emailing is safe to hand out: the server refuses a draft, a rejected draft or one still
  // pending activation whoever asks, so only an activated invoice can go out.
  P.INVOICE.SEND,

  // Work assignment and the notifications that announce it
  P.TASKS.READ,
  P.TASKS.CREATE,
  P.TASKS.UPDATE,
  P.TASKS.DELETE,
  P.NOTIFICATIONS.READ,
  P.NOTIFICATIONS.CREATE,
  P.NOTIFICATIONS.UPDATE,
  P.FOLLOWUP.READ,
  P.FOLLOWUP.CREATE,
  P.FOLLOWUP.UPDATE,
  P.FOLLOWUP.DELETE,
  P.FOLLOWUP.ADMIN,

  // Colleagues they assign work to and book appointments with
  P.USERS.READ,

  // Catalogues they maintain, plus sending college documents out for signing
  P.UNIVERSITY.READ,
  P.UNIVERSITY.CREATE,
  P.UNIVERSITY.UPDATE,
  P.UNIVERSITY.DELETE,
  P.UNIVERSITY_DOCUMENT.READ,
  P.UNIVERSITY_DOCUMENT.CREATE,
  P.UNIVERSITY_DOCUMENT.UPDATE,
  P.UNIVERSITY_DOCUMENT.SEND,
  P.COURSE.READ,
  P.COURSE.CREATE,
  P.COURSE.UPDATE,
  P.COURSE.DELETE,
  P.RPL_COURSE.READ,
  P.RPL_COURSE.CREATE,
  P.RPL_COURSE.UPDATE,
  P.RPL_COURSE.DELETE,
  P.PRICELIST.READ,
  P.PRICELIST.CREATE,
  P.PRICELIST.UPDATE,
  P.PRICELIST.DELETE,
  P.CHECKLIST.READ,
  P.CHECKLIST.CREATE,
  P.CHECKLIST.UPDATE,
  P.CHECKLIST.DELETE,

  // The whole email module, including templates and the sending configuration
  P.EMAIL.MESSAGE_READ,
  P.EMAIL.MESSAGE_COMPOSE,
  P.EMAIL.MESSAGE_SEND,
  P.EMAIL.TEMPLATE_READ,
  P.EMAIL.TEMPLATE_CREATE,
  P.EMAIL.TEMPLATE_UPDATE,
  P.EMAIL.TEMPLATE_DELETE,
  P.EMAIL.CONFIG_READ,
  P.EMAIL.CONFIG_UPDATE,
  P.EMAIL.LOG_READ,

  // Appointments and reminders, deletion included. The backend scopes both: reminder queries
  // filter on the creator, and appointment reads and deletes go through the visibility check,
  // so a private appointment stays invisible to anyone who is not its creator or attendee.
  P.APPOINTMENTS.READ,
  P.APPOINTMENTS.CREATE,
  P.APPOINTMENTS.UPDATE,
  P.APPOINTMENTS.DELETE,
  P.REMINDERS.READ,
  P.REMINDERS.CREATE,
  P.REMINDERS.UPDATE,
  P.REMINDERS.DELETE,

  // Settings is the container for bank and MARA details, which they read but do not maintain
  P.SYSTEM.SETTINGS_READ,
  P.BANK_ACCOUNT.READ,
  P.MARA_AGENT.READ,

  // Check-in and client-details forms shared with clients
  P.FORMS.INTAKE_READ,
  P.FORMS.INTAKE_UPDATE,

  // PR enquiries: follow them up, share the form and convert them into clients. Deleting one
  // stays with Super admin, as with the other case records.
  P.PR_ENQUIRY.READ,
  P.PR_ENQUIRY.CREATE,
  P.PR_ENQUIRY.UPDATE,
  P.PR_ENQUIRY.SHARE,

  // Referral partners: onboard them, see the leads they send, handle the applications they
  // request and correspond with them. Activating or rejecting a lead, suspending a partner and
  // re-pointing a client's referrer decide who gets paid, so those stay with Super admin.
  P.REFERRAL.PARTNER_READ,
  P.REFERRAL.PARTNER_INVITE,
  P.REFERRAL.PARTNER_UPDATE,
  P.REFERRAL.LEAD_READ,
  P.REFERRAL.APPLICATION_READ,
  P.REFERRAL.APPLICATION_REVIEW,
  P.REFERRAL.MESSAGE_READ,
  P.REFERRAL.MESSAGE_SEND,
  P.REFERRAL.ATTRIBUTION_ASSIGN,
];

/**
 * Everything ACCOUNT adds on top of ADMIN: approving money, and the ledgers behind it.
 *
 * Commission stays here as a policy choice: admissions:commission:* covers bonus as well as
 * commission, because it backs the manual claim action on an admission term, and those figures
 * are the accounts team's to see and set. Nothing in the backend stops an admin from holding the
 * key, so grant it individually if a particular admin does need it.
 */
const ACCOUNT_EXTRA_KEYS = [
  P.ACCOUNTS.READ,
  P.ACCOUNTS.CREATE,
  P.ACCOUNTS.UPDATE,
  P.ACCOUNTS.PAYMENT_VERIFY,
  P.ACCOUNTS.PAYMENT_MODIFY,
  P.ACCOUNTS.VERIFICATION_VERIFY,
  P.ACCOUNTS.PROVIDER_READ,
  P.ACCOUNTS.PROVIDER_CREATE,
  P.ACCOUNTS.PROVIDER_UPDATE,

  P.ADMISSIONS.COMMISSION_READ,
  P.ADMISSIONS.COMMISSION_CREATE,
  P.ADMISSIONS.COMMISSION_UPDATE,

  P.PROFIT_LOSS.READ,

  P.INVOICE.READ,
  P.INVOICE.CREATE,
  P.INVOICE.UPDATE,
  P.INVOICE.VERIFY,

  P.REFERRAL.READ,
  P.REFERRAL.CREATE,
  P.REFERRAL.UPDATE,

  // What a partner is owed and paid: the ledger, signing a figure off, and recording the payout
  P.REFERRAL.COMMISSION_READ,
  P.REFERRAL.COMMISSION_APPROVE,
  P.REFERRAL.PAYOUT_CREATE,

  // Admins read bank and MARA details; the accounts team maintains them
  P.BANK_ACCOUNT.CREATE,
  P.BANK_ACCOUNT.UPDATE,
  P.BANK_ACCOUNT.DELETE,
  P.MARA_AGENT.CREATE,
  P.MARA_AGENT.UPDATE,
  P.MARA_AGENT.DELETE,
];

const unique = (keys) => [...new Set(keys)];

const ACCOUNT_KEYS = unique([...ADMIN_KEYS, ...ACCOUNT_EXTRA_KEYS]);

/** Roles offered in the drawer's preset picker, in escalating order. */
export const ROLE_PRESETS = [
  {
    role: "ADMIN",
    label: "Admin",
    summary:
      "Runs case work end to end across clients, admissions, visa, insurance, RPL and skill assessment, with the catalogues, email, scheduling and follow-ups that go with it. Assigns case officers, and onboards referral partners, reviews their application requests and messages them. Records and refunds payments and watches their verification status, and emails activated invoices, but cannot approve a payment or activate an invoice.",
    keys: ADMIN_KEYS,
  },
  {
    role: "ACCOUNT",
    label: "Account",
    summary:
      "Everything Admin does, plus the money side: approving payments and verifications, commission and bonus claims, profit and loss, invoices, referrals, partner commission and payouts, providers, and maintaining bank and MARA details.",
    keys: ACCOUNT_KEYS,
  },
  {
    role: "SUPER_ADMIN",
    label: "Super admin",
    summary:
      "Every organisation permission in this drawer. Platform permissions are granted separately.",
    keys: null, // resolved against the live catalog, so it never falls behind a new key
  },
  {
    role: "REFERRAL_USER",
    label: "Referral",
    summary:
      "No permissions at all. A referral partner works only in the partner portal, which is reached by their REFERRAL_USER role and never by these keys — granting any of them opens admin screens holding other clients, commissions, bonuses and profit and loss.",
    keys: [],
  },
  {
    role: "NONE",
    label: "No permissions",
    summary: "Clears every toggle. Use it as a blank slate for a role that is granted key by key.",
    keys: [],
  },
];

export const findRolePreset = (role) =>
  ROLE_PRESETS.find((preset) => preset.role === String(role || "").toUpperCase()) || null;

/**
 * Keys a preset should switch on, intersected with what this drawer actually offers, so a key
 * the backend has retired can never be re-selected. SUPER_ADMIN resolves to the whole catalog.
 */
export const resolveRolePresetKeys = (preset, availableKeys = []) => {
  if (!preset) {
    return [];
  }
  if (preset.keys === null) {
    return [...availableKeys];
  }
  const available = new Set(availableKeys);
  return preset.keys.filter((key) => available.has(key));
};

/** Preset keys this drawer cannot offer, so the UI can say so instead of silently dropping them. */
export const missingRolePresetKeys = (preset, availableKeys = []) => {
  if (!preset || preset.keys === null) {
    return [];
  }
  const available = new Set(availableKeys);
  return preset.keys.filter((key) => !available.has(key));
};
